/**
 * Collect variant + line item property context at AI upload time (Zalify theme).
 */
(function () {
  "use strict";

  function findProductContainer(scope) {
    const root =
      scope instanceof Element
        ? scope
        : scope instanceof Document
          ? scope.documentElement
          : document;

    if (root.matches?.(".ai-upload-section")) {
      return (
        root.closest(
          ".product-form, .product, .shopify-section--main-product, quick-buy-drawer, .drawer__content"
        ) || root
      );
    }

    return (
      root.closest?.(
        ".product-form, .product, .shopify-section--main-product, quick-buy-drawer, .drawer__content"
      ) ||
      root.querySelector?.(
        ".product-form, .product, .shopify-section--main-product, quick-buy-drawer"
      ) ||
      root
    );
  }

  function getUploadSection(scope) {
    if (scope instanceof Element && scope.matches(".ai-upload-section")) {
      return scope;
    }

    const container = findProductContainer(scope);
    return container.querySelector?.(".ai-upload-section");
  }

  function getFormForUpload(scope) {
    const uploadSection = getUploadSection(scope);
    const container = findProductContainer(scope);

    const formId = uploadSection?.dataset.formId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }

    const hidden = uploadSection?.querySelector("#ai-custom-image-url");
    const linkedId = hidden?.getAttribute("form");
    if (linkedId) {
      const linked = document.getElementById(linkedId);
      if (linked) return linked;
    }

    return (
      container.querySelector?.('form[id*="product-form"]') ||
      uploadSection?.closest('form[id*="product-form"]') ||
      container.closest?.('form[id*="product-form"]')
    );
  }

  function normalizeOptionName(name) {
    return String(name || "")
      .trim()
      .replace(/:+\s*$/u, "");
  }

  function extractPropertyName(fieldName) {
    const match = String(fieldName || "").match(/^properties\[(.+)\]$/);
    return match ? match[1] : "";
  }

  function getProductGenerationConfig(scope) {
    const uploadSection = getUploadSection(scope);
    const script = uploadSection?.querySelector(".product-generation-config");
    if (!script?.textContent) return {};

    try {
      const config = JSON.parse(script.textContent);
      return config && typeof config === "object" ? config : {};
    } catch {
      return {};
    }
  }

  function getProductOptionNames(scope) {
    const config = getProductGenerationConfig(scope);
    return Array.isArray(config.productOptions)
      ? config.productOptions.filter(Boolean)
      : [];
  }

  function findVariantById(variants, variantId) {
    if (!variantId) return null;
    const id = String(variantId);
    return (
      (Array.isArray(variants) ? variants : []).find(
        (variant) => String(variant?.id) === id
      ) || null
    );
  }

  function buildVariantOptionsFromVariant(variant, optionNames) {
    if (!variant) return [];

    const values = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3];

    /** @type {{ name: string, value: string }[]} */
    const options = [];

    values.forEach((value, index) => {
      const normalized = String(value ?? "").trim();
      if (!normalized) return;

      options.push({
        name:
          normalizeOptionName(optionNames[index]) || `选项${index + 1}`,
        value: normalized,
      });
    });

    return options;
  }

  function getVariantId(scope) {
    const form = getFormForUpload(scope);
    return String(form?.querySelector('input[name="id"]')?.value || "");
  }

  function collectVariantOptions(scope) {
    const config = getProductGenerationConfig(scope);
    const variantId = getVariantId(scope);
    const variant = findVariantById(config.productVariants, variantId);
    return buildVariantOptionsFromVariant(variant, getProductOptionNames(scope));
  }

  function enrichVariantContext(scope, context) {
    if (!context || typeof context !== "object") return context;

    const config = getProductGenerationConfig(scope);
    const variantId = String(context.variantId || getVariantId(scope) || "");
    const variant = findVariantById(config.productVariants, variantId);
    const optionNames = getProductOptionNames(scope);

    if (
      (!Array.isArray(context.variantOptions) ||
        !context.variantOptions.length) &&
      variant
    ) {
      context.variantOptions = buildVariantOptionsFromVariant(
        variant,
        optionNames
      );
    }

    if (!context.variantTitle && variant?.title) {
      context.variantTitle = String(variant.title);
    } else if (
      !context.variantTitle &&
      Array.isArray(context.variantOptions) &&
      context.variantOptions.length
    ) {
      context.variantTitle = buildVariantTitle(context.variantOptions);
    }

    if (!context.variantId && variantId) {
      context.variantId = variantId;
    }

    return context;
  }

  function buildVariantTitle(variantOptions) {
    if (!Array.isArray(variantOptions) || !variantOptions.length) {
      return "";
    }
    return variantOptions.map((option) => option.value).join(" / ");
  }

  function collectCustomProperties(scope) {
    const container = findProductContainer(scope);
    const form = getFormForUpload(scope);
    const searchRoot = form || container;
    /** @type {Record<string, string>} */
    const values = {};

    searchRoot
      .querySelectorAll(".product-form__line-item-property")
      .forEach((propertyEl) => {
        const checkbox = propertyEl.querySelector('input[type="checkbox"]');
        const textarea = propertyEl.querySelector(
          'textarea[name^="properties["]'
        );
        const textInput = propertyEl.querySelector(
          'input[type="text"][name^="properties["]'
        );

        if (checkbox instanceof HTMLInputElement) {
          const key = extractPropertyName(checkbox.name);
          if (!key || key.startsWith("_")) return;
          if (checkbox.checked) {
            values[key] = String(checkbox.value || "Yes").trim();
          }
          return;
        }

        const control =
          textarea instanceof HTMLTextAreaElement
            ? textarea
            : textInput instanceof HTMLInputElement
              ? textInput
              : null;

        if (!control) return;

        const key = extractPropertyName(control.name);
        if (!key || key.startsWith("_")) return;

        const value = control.value.trim();
        if (value) {
          values[key] = value;
        }
      });

    return values;
  }

  function validateLineItemProperties(scope) {
    const container = findProductContainer(scope);
    const form = getFormForUpload(scope);
    const searchRoot = form || container;
    /** @type {{ field: Element, control: HTMLElement, label: string }[]} */
    const errors = [];

    searchRoot
      .querySelectorAll(".product-form__line-item-property")
      .forEach((propertyEl) => {
        const requiredControl = propertyEl.querySelector("[required]");
        if (!(requiredControl instanceof HTMLElement)) return;

        let valid = true;
        if (requiredControl instanceof HTMLInputElement) {
          if (requiredControl.type === "checkbox") {
            valid = requiredControl.checked;
          } else {
            valid = requiredControl.value.trim() !== "";
          }
        } else if (requiredControl instanceof HTMLTextAreaElement) {
          valid = requiredControl.value.trim() !== "";
        }

        if (!valid) {
          const label =
            propertyEl.querySelector(".input__block-label")?.textContent ||
            propertyEl.querySelector("label")?.textContent ||
            "Custom field";
          errors.push({
            field: propertyEl,
            control: requiredControl,
            label: String(label).replace(/:\s*$/, "").trim(),
          });
        }
      });

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  function namesMatch(left, right) {
    return (
      normalizeOptionName(left).toLowerCase() ===
      normalizeOptionName(right).toLowerCase()
    );
  }

  function waitForVariantUpdate(timeoutMs = 2500) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        document.removeEventListener("variant:changed", onUpdate);
        clearTimeout(timer);
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      };

      const onUpdate = () => finish();
      const timer = setTimeout(finish, timeoutMs);
      document.addEventListener("variant:changed", onUpdate);
    });
  }

  function applyCustomProperties(scope, customProperties) {
    if (!customProperties || typeof customProperties !== "object") return;

    const container = findProductContainer(scope);
    const form = getFormForUpload(scope);
    const searchRoot = form || container;

    Object.entries(customProperties).forEach(([heading, value]) => {
      const propertyEl = Array.from(
        searchRoot.querySelectorAll(".product-form__line-item-property")
      ).find((el) => {
        const label =
          el.querySelector(".input__block-label")?.textContent ||
          el.querySelector("label")?.textContent ||
          "";
        return namesMatch(label.replace(/:\s*$/, ""), heading);
      });

      if (!propertyEl) return;

      const text = String(value ?? "").trim();
      const checkbox = propertyEl.querySelector('input[type="checkbox"]');
      const textarea = propertyEl.querySelector(
        'textarea[name^="properties["]'
      );
      const textInput = propertyEl.querySelector(
        'input[type="text"][name^="properties["]'
      );

      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = Boolean(text);
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      } else if (textarea instanceof HTMLTextAreaElement) {
        textarea.value = text;
        textarea.dispatchEvent(new Event("input", { bubbles: true }));
      } else if (textInput instanceof HTMLInputElement) {
        textInput.value = text;
        textInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
  }

  async function applyVariantOptions(scope, context) {
    const container = findProductContainer(scope);
    const form = getFormForUpload(scope);
    const productVariants = container.querySelector("product-variants");
    const options = Array.isArray(context?.variantOptions)
      ? context.variantOptions
      : [];

    if (!options.length) {
      if (context?.variantId && form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput instanceof HTMLInputElement) {
          idInput.value = String(context.variantId);
          idInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      return;
    }

    let changed = false;

    options.forEach(({ name, value }, index) => {
      if (!value) return;
      const targetValue = String(value).trim();
      const optionIndex = index + 1;

      const select = form?.querySelector(`select[name="option${optionIndex}"]`);
      if (select instanceof HTMLSelectElement) {
        if (select.value.trim() !== targetValue) {
          select.value = targetValue;
          changed = true;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }

      const radios = form?.querySelectorAll(
        `input[name="option${optionIndex}"]`
      );
      for (const radio of radios) {
        if (!(radio instanceof HTMLInputElement)) continue;
        if (radio.value.trim() !== targetValue) continue;
        if (!radio.checked) {
          radio.checked = true;
          changed = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }

      if (name) {
        const selectors = container.querySelectorAll(
          ".product-form__option-selector"
        );
        for (const selector of selectors) {
          const optionNameEl = selector.querySelector(
            ".product-form__option-name"
          );
          const optionName = (
            optionNameEl?.textContent || ""
          )
            .replace(/:\s*$/, "")
            .trim();
          if (optionName && !namesMatch(optionName, name)) continue;

          const optionSelect = selector.querySelector(
            'select[name^="option"]'
          );
          if (optionSelect instanceof HTMLSelectElement) {
            if (optionSelect.value.trim() !== targetValue) {
              optionSelect.value = targetValue;
              changed = true;
              optionSelect.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return;
          }

          const optionRadios = selector.querySelectorAll(
            'input[type="radio"][name^="option"]'
          );
          for (const radio of optionRadios) {
            if (!(radio instanceof HTMLInputElement)) continue;
            if (radio.value.trim() !== targetValue) continue;
            if (!radio.checked) {
              radio.checked = true;
              changed = true;
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return;
          }
        }
      }
    });

    if (context?.variantId && form) {
      const idInput = form.querySelector('input[name="id"]');
      if (
        idInput instanceof HTMLInputElement &&
        idInput.value !== String(context.variantId)
      ) {
        if (productVariants && typeof productVariants.selectVariant === "function") {
          productVariants.selectVariant(Number(context.variantId));
          changed = true;
        } else {
          idInput.value = String(context.variantId);
          idInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }

    if (changed) {
      await waitForVariantUpdate();
    }
  }

  /**
   * @param {Document|Element|null|undefined} [scope]
   */
  window.getAiGenerationContext = function (scope) {
    const resolvedScope = scope || document;
    const variantId = getVariantId(resolvedScope);
    const variantOptions = collectVariantOptions(resolvedScope);
    const config = getProductGenerationConfig(resolvedScope);
    const variant = findVariantById(config.productVariants, variantId);
    const variantTitle =
      buildVariantTitle(variantOptions) ||
      (variant?.title ? String(variant.title) : "");

    return {
      variantId,
      variantTitle,
      variantOptions,
      customProperties: collectCustomProperties(resolvedScope),
    };
  };

  window.validateAiLineItemProperties = function (scope) {
    return validateLineItemProperties(scope || document);
  };

  /**
   * @param {Document|Element|null|undefined} scope
   * @param {{
   *   variantId?: string,
   *   variantTitle?: string,
   *   variantOptions?: { name: string, value: string }[],
   *   customProperties?: Record<string, string>,
   * } | null | undefined} context
   */
  window.applyAiGenerationContext = async function (scope, context) {
    if (!context) return;

    const resolvedScope = scope || document;
    const enrichedContext = enrichVariantContext(resolvedScope, {
      ...context,
      customProperties: { ...(context.customProperties || {}) },
      variantOptions: Array.isArray(context.variantOptions)
        ? [...context.variantOptions]
        : [],
    });

    applyCustomProperties(resolvedScope, enrichedContext.customProperties || {});

    if (
      (Array.isArray(enrichedContext.variantOptions) &&
        enrichedContext.variantOptions.length) ||
      enrichedContext.variantId
    ) {
      await applyVariantOptions(resolvedScope, enrichedContext);
    }
  };
})();
